import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  Typography,
  Divider,
  Fade,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { createHoliday, updateHoliday } from '../../services/companyHolidayService';
import { useStyles } from './holidayListPageStyles';

export default function HolidayAddEditModel({ open, onClose, onAddHoliday, onUpdateHoliday, holidayToEdit }) {
  const classes = useStyles();

  useEffect(() => {
    if (holidayToEdit) {
      formik.setValues({
        name: holidayToEdit.name,
        date: dayjs(holidayToEdit.date).startOf('day'), // Ensure the date is set to the start of the day
      });
    } else {
      formik.resetForm();
    }
  }, [holidayToEdit, open]);

  // Form validation schema using Yup
  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Holiday name is required')
      .min(3, 'Name should be at least 3 characters')
      .max(50, 'Name should not exceed 50 characters'),
    date: Yup.date().required('Holiday date is required'),
  });

  // Initialize formik
  const formik = useFormik({
    initialValues: {
      name: '',
      date: dayjs(),
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        const holidayData = {
          name: values.name.trim(),
          date: values.date.format('YYYY-MM-DD'), // Format the date as YYYY-MM-DD
        };

        if (holidayToEdit) {
          const response = await updateHoliday(holidayToEdit.id, holidayData);
          onUpdateHoliday(response?.data?.data);
          toast.success(response?.data?.message);
        } else {
          const response = await createHoliday(holidayData);
          onAddHoliday(response?.data?.data);
          toast.success(response?.data?.message);
        }
        handleClose();
      } catch (error) {
        toast.error(error?.response?.data?.message);
        console.error(error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 24,
      }}
      TransitionComponent={Fade}
    >
      <Box className={classes.dialogTitle}>
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {holidayToEdit ? 'Edit Holiday' : 'Add New Holiday'}
          </Typography>
        </DialogTitle>
      </Box>

      <Divider />

      <DialogContent>
        <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
          <Box className={classes.form}>
            <TextField
              autoFocus
              name="name"
              label="Holiday Name"
              type="text"
              fullWidth
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              placeholder="Enter holiday name"
              variant="outlined"
              sx={{ mb: 3 }}
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Holiday Date"
                value={formik.values.date}
                onChange={(newValue) => {
                  if (newValue) {
                    formik.setFieldValue('date', dayjs(newValue).startOf('day'));
                  } else {
                    formik.setFieldValue('date', null);
                  }
                }}
                inputFormat="DD MMM YYYY"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="date"
                    error={formik.touched.date && Boolean(formik.errors.date)}
                    helperText={formik.touched.date && formik.errors.date}
                    fullWidth
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                )}
                className={classes.datePicker}
              />
            </LocalizationProvider>

            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              Ensure the holiday name matches exactly as it will appear to users.
            </Typography>
          </Box>

          <DialogActions className={classes.actions}>
            <Button onClick={handleClose} disabled={formik.isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={formik.isSubmitting || !formik.isValid}
              startIcon={formik.isSubmitting ? <CircularProgress size={20} /> : null}
              sx={{ ml: 2 }}
            >
              {holidayToEdit ? 'Update Holiday' : 'Add Holiday'}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
}
